import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faWifi, faSyringe, faClock, faInfoCircle, faSave } from '@fortawesome/free-solid-svg-icons';
import { getBeneficiaries, immunizationStore } from '../services/authService';
import { showSuccess, showError } from '../utils/alert';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
function ImmunizationAdd() {
  // Initial form state
  const initialFormState = {
    vaccineType: '',
    batchNumber: '',
    expiryDate: '',
    adminDate: '',
    adminSite: '',
    adminMethod: '',
    administrator: '',
    adverseReaction: '',
    reactionNotes: '',
    followUpCall: false,
    nextSchedule: false,
    nextVaccine: '',
    nextDate: '',
    parentInstructions: '',
  };

  // State management
  const [formData, setFormData] = useState(initialFormState);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [ashaId] = useState(2); // Can be dynamically set from login context
  const [beneficiary, setBeneficiary] = useState(null);
  const [beneficiaryList, setBeneficiaryList] = useState([]);
  const [showUserForm, setShowUserForm] = useState(false);

 const selectedVillageId = useSelector((state) => state.village.selectedVillageId);
  const navigate = useNavigate();
  // Fetch beneficiaries on mount
  useEffect(() => {
    async function fetchBeneficiaries() {
      try {
        const asha_id = localStorage.getItem('asha_id');
        const villageID = selectedVillageId;
         const getBen = await getBeneficiaries(asha_id,villageID );
           if (getBen.status === true) {
           showSuccess(getBen.message || 'Beneficiaries fetched successfully!');
          const formattedList = getBen.data.map(b => ({
            id: b.beneficiary_id,
            name: b.name,  
            age: b.age,
            gender: b.gender,
            condition: b.category,
            village: b.village_name,
          }));

          setBeneficiaryList(formattedList);

          if (formattedList.length > 0) {
            setBeneficiary(formattedList[0]);
          }
         } else {
          showError(getBen.message || 'No beneficiaries found');    
           }
      } catch (err) {
        showError('Network error while fetching beneficiaries');
        console.error('Fetch error:', err);
      }
    }

    fetchBeneficiaries();
  }, [selectedVillageId]);

  // Handle input changes
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  // Handle beneficiary selection
  const handleBeneficiaryChange = (e) => {
    const selected = beneficiaryList.find(b => b.name === e.target.value);
    if (selected) {
      setBeneficiary(selected);
      setShowUserForm(false);
    }
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    // Validation for required fields
    const requiredFields = [
      'vaccineType',
      'batchNumber',
      'expiryDate',
      'adminDate',
      'adminSite',
      'adminMethod',
      'administrator',
      'adverseReaction',
    ];
    const missingFields = requiredFields.filter(field => !formData[field]);

    if (missingFields.length > 0 || !beneficiary) {
      setError(
        `Please fill in all required fields: ${missingFields.join(', ')}${!beneficiary ? ' and select a beneficiary' : ''}.`
      );
      return;
    }
    const payload = {
      ...formData,
      beneficiaryId: beneficiary.id,
      ashaId,
    };


    try {
      const response = await immunizationStore(payload);

      if (response.status === true) {
       showSuccess(response.message || 'Immunization record saved successfully!');
      setFormData(initialFormState);
      setShowUserForm(false);
      if (beneficiaryList.length > 0) {
        setBeneficiary(beneficiaryList[0]);
      }
      navigate('/immunization');  
    } 
    } catch (err) {
      setError(err.message || 'An error occurred while saving the record.');
      showError(err.message || 'An error occurred while saving the record.');  
    } 
  };

  // Handle cancel
  const handleCancel = () => {
    setFormData(initialFormState);
    setError('');
    setSuccess('');
    setShowUserForm(false);
    if (beneficiaryList.length > 0) {
      setBeneficiary(beneficiaryList[0]);
    }
  };  

  return (
    <>
      <div className="page-header">
        <button className="back-button">
          <FontAwesomeIcon icon={faArrowLeft} />
          Back
        </button>
        <div className="page-title">New Immunization Record</div>
      </div>

      {error && <div className="error-message">{error}</div>}
      {success && <div className="success-message">{success}</div>}

      <div className="form-card">
        {/* Beneficiary Selection */}
        {beneficiary && (
          <div className="form-section">
            <div className="section-title">Select Beneficiary</div>
            <div className="beneficiary-card">
              <div className="beneficiary-info">
                <div className="beneficiary-name">{beneficiary.name}</div>
                <div className="beneficiary-details">
                  {beneficiary.age} years • {beneficiary.gender} • {beneficiary.condition}
                </div>
                <div className="beneficiary-village">{beneficiary.village}</div>
              </div>
              <button className="change-button" onClick={() => setShowUserForm(!showUserForm)}>
                Change
              </button>
            </div>

            {showUserForm && (
              <div className="form-section">
                <label>Select a Beneficiary</label>
                <select
                  onChange={handleBeneficiaryChange}
                  value={beneficiary.name}
                >
                  <option value="" disabled>Select beneficiary...</option>
                  {beneficiaryList.map((b) => (
                    <option key={b.id} value={b.name}>
                      {b.name} - {b.age} yrs - {b.village}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>
        )}

        <div className="immunization-history">
          <div className="history-item">
            <div className="history-item-header">
              <div className="history-item-title">BCG</div>
              <div className="history-item-date">Jan 15, 2024</div>
            </div>
            <div className="history-item-details">
              Batch: BCG123456 • Administered by Dr. Sharma
            </div>
          </div>
          <div className="history-item">
            <div className="history-item-header">
              <div className="history-item-title">OPV-0</div>
              <div className="history-item-date">Jan 15, 2024</div>
            </div>
            <div className="history-item-details">
              Batch: OPV789123 • Administered by Dr. Sharma
            </div>
          </div>
          <div className="history-item">
            <div className="history-item-header">
              <div className="history-item-title">DPT-1</div>
              <div className="history-item-date">Feb 20, 2024</div>
            </div>
            <div className="history-item-details">
              Batch: DPT456789 • Administered by ASHA Worker Sunita
            </div>
          </div>
        </div>

        <div className="due-date-card">
          <div className="due-date-title">Next Due Vaccination</div>
          <div className="due-date-info">
            <FontAwesomeIcon icon={faSyringe} />
            <div className="due-date-text">
              <strong>DPT-2</strong> is due on <strong>April 20, 2025</strong> (3 days from now)
              <div className="status-indicator due">
                <FontAwesomeIcon icon={faClock} />
                Due Soon
              </div>
            </div>
          </div>
        </div>

        <hr className="divider" />

        <div className="form-section">
          <div className="section-title">Vaccine Information</div>

          <div className="form-field">
            <label htmlFor="vaccine-type">Vaccine Type</label>
            <select
              id="vaccine-type"
              name="vaccineType"
              className="vaccine-selector"
              value={formData.vaccineType}
              onChange={handleChange}
            >
              <option value="dpt-2">DPT-2 (Diphtheria, Pertussis, Tetanus)</option>
              <option value="opv-2">OPV-2 (Oral Polio Vaccine)</option>
              <option value="hepb-2">HepB-2 (Hepatitis B)</option>
              <option value="hib-2">Hib-2 (Haemophilus influenzae type B)</option>
              <option value="pneumo-2">PCV-2 (Pneumococcal Conjugate Vaccine)</option>
              <option value="rota-2">Rotavirus-2</option>
            </select>
          </div>

          <div className="vaccine-info">
            <FontAwesomeIcon icon={faInfoCircle} />
            <div className="vaccine-info-text">
              <h4>DPT-2 Information</h4>
              <p>Second dose in the DPT series. Protects against Diphtheria, Pertussis (Whooping Cough), and Tetanus. Typically administered at 10 weeks of age.</p>
            </div>
          </div>

          <div className="input-group">
            <div className="form-field">
              <label htmlFor="vaccine-batch">Batch Number</label>
              <input
                type="text"
                id="vaccine-batch"
                name="batchNumber"
                className="text-input"
                value={formData.batchNumber}
                onChange={handleChange}
              />
              <div className="hint">Enter the batch number from the vaccine vial</div>
            </div>
            <div className="form-field">
              <label htmlFor="vaccine-expiry">Expiration Date</label>
              <input
                type="date"
                id="vaccine-expiry"
                name="expiryDate"
                className="date-picker"
                value={formData.expiryDate}
                onChange={handleChange}
              />
            </div>
          </div>
        </div>

        <hr className="divider" />

        <div className="form-section">
          <div className="section-title">Administration Details</div>

          <div className="form-field">
            <label htmlFor="admin-date">Administration Date</label>
            <input
              type="date"
              id="admin-date"
              name="adminDate"
              className="date-picker"
              value={formData.adminDate}
              onChange={handleChange}
            />
          </div>

          <div className="form-field">
            <label>Administration Site</label>
            <div className="radio-group">
              <div className="radio-option">
                <input
                  type="radio"
                  id="left-thigh"
                  name="adminSite"
                  value="left-thigh"
                  checked={formData.adminSite === 'left-thigh'}
                  onChange={handleChange}
                />
                <label htmlFor="left-thigh">Left Thigh</label>
              </div>
              <div className="radio-option">
                <input
                  type="radio"
                  id="right-thigh"
                  name="adminSite"
                  value="right-thigh"
                  checked={formData.adminSite === 'right-thigh'}
                  onChange={handleChange}
                />
                <label htmlFor="right-thigh">Right Thigh</label>
              </div>
              <div className="radio-option">
                <input
                  type="radio"
                  id="left-arm"
                  name="adminSite"
                  value="left-arm"
                  checked={formData.adminSite === 'left-arm'}
                  onChange={handleChange}
                />
                <label htmlFor="left-arm">Left Upper Arm</label>
              </div>
              <div className="radio-option">
                <input
                  type="radio"
                  id="right-arm"
                  name="adminSite"
                  value="right-arm"
                  checked={formData.adminSite === 'right-arm'}
                  onChange={handleChange}
                />
                <label htmlFor="right-arm">Right Upper Arm</label>
              </div>
            </div>
          </div>

          <div className="form-field">
            <label htmlFor="admin-method">Administration Method</label>
            <select
              id="admin-method"
              name="adminMethod"
              className="vaccine-selector"
              value={formData.adminMethod}
              onChange={handleChange}
            >
              <option value="intramuscular">Intramuscular Injection</option>
              <option value="subcutaneous">Subcutaneous Injection</option>
              <option value="intradermal">Intradermal Injection</option>
              <option value="oral">Oral Administration</option>
            </select>
          </div>

          <div className="form-field">
            <label htmlFor="administrator">Administered By</label>
            <input
              type="text"
              id="administrator"
              name="administrator"
              className="text-input"
              value={formData.administrator}
              onChange={handleChange}
            />
          </div>
        </div>

        <hr className="divider" />

        <div className="form-section">
          <div className="section-title">Post-Vaccination Assessment</div>

          <div className="form-field">
            <label>Were there any immediate adverse reactions?</label>
            <div className="radio-group">
              <div className="radio-option">
                <input
                  type="radio"
                  id="reaction-yes"
                  name="adverseReaction"
                  value="yes"
                  checked={formData.adverseReaction === 'yes'}
                  onChange={handleChange}
                />
               <label htmlFor="reaction-yes">Yes</label>
              </div>
              <div className="radio-option">
                <input
                  type="radio"
                  id="reaction-no"
                  name="adverseReaction"
                  value="no"
                  checked={formData.adverseReaction === 'no'}
                  onChange={handleChange}
                />
                <label htmlFor="reaction-no">No</label>
              </div>
            </div>
          </div>

          <div className="form-field">
            <label htmlFor="reaction-notes">Observation Notes</label>
            <textarea
              id="reaction-notes"
              name="reactionNotes"
              className="text-area"
              placeholder="Describe any reactions or observations..."
              value={formData.reactionNotes}
              onChange={handleChange}
            />
          </div>

          <div className="form-field">
            <label>Follow-up Requirements</label>
            <div className="checkbox-option">
              <input
                type="checkbox"
                id="follow-up-call"
                name="followUpCall"
                checked={formData.followUpCall}
                onChange={handleChange}
              />
              <label htmlFor="follow-up-call">Follow-up call in 24 hours</label>
            </div>
            <div className="checkbox-option">
              <input
                type="checkbox"
                id="next-schedule"
                name="nextSchedule"
                checked={formData.nextSchedule}
                onChange={handleChange}
              />
              <label htmlFor="next-schedule">Schedule next vaccination</label>
            </div>
          </div>
        </div>

        <hr className="divider" />

        <div className="form-section">
          <div className="section-title">Next Vaccination</div>

          <div className="form-field">
            <label htmlFor="next-vaccine">Next Vaccine Due</label>
            <select
              id="next-vaccine"
              name="nextVaccine"
              className="vaccine-selector"
              value={formData.nextVaccine}
              onChange={handleChange}
            >
              <option value="dpt-3">DPT-3 (Diphtheria, Pertussis, Tetanus)</option>
              <option value="opv-3">OPV-3 (Oral Polio Vaccine)</option>
              <option value="hepb-3">HepB-3 (Hepatitis B)</option>
              <option value="hib-3">Hib-3 (Haemophilus influenzae type B)</option>
              <option value="pneumo-3">PCV-3 (Pneumococcal Conjugate Vaccine)</option>
              <option value="rota-3">Rotavirus-3</option>
            </select>
          </div>

          <div className="form-field">
            <label htmlFor="next-date">Due Date</label>
            <input
              type="date"
              id="next-date"
              name="nextDate"
              className="date-picker"
              value={formData.nextDate}
              onChange={handleChange}
            />
            <div className="hint">Typically 8 weeks after current dose</div>
          </div>

          <div className="form-field">
            <label htmlFor="parent-instructions">Instructions for Parents</label>
            <textarea
              id="parent-instructions"
              name="parentInstructions"
              className="text-area"
              value={formData.parentInstructions}
              onChange={handleChange}
            />
          </div>
        </div>

        <div className="action-buttons">
          <button className="cancel-button" onClick={handleCancel}>Cancel</button>
          <button className="save-button" onClick={handleSubmit}>
            <FontAwesomeIcon icon={faSave} />
            Save Record
          </button>
        </div>
      </div>
    </>
  );
}

export default ImmunizationAdd;